import React from "react";
import { CopyIcon } from "lucide-react";

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { useTranslate } from "@/hooks";
import { cn } from "@/helpers/tailwind";

interface CopyToClipboardButtonProps {
  className?: string;
  copyText: string;
  variant?: "simple" | "chip";
  displayText: string;
}

const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = ({
  className,
  copyText,
  variant = "simple",
  displayText,
}) => {
  const t = useTranslate();
  const [tooltipContent, setTooltipContent] = React.useState(
    t("common.other.click_to_copy")
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const [isClicked, setIsClicked] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    navigator.clipboard.writeText(copyText ?? "");
    setTooltipContent(t("common.other.copied"));
    setIsClicked(true);
    setIsOpen(true);

    setTimeout(() => {
      setIsOpen(false);
      setIsClicked(false);
    }, 1000);
    setTimeout(() => {
      setTooltipContent(t("common.other.click_to_copy"));
    }, 1100);
  }, [copyText]);

  return (
    <TooltipProvider>
      <Tooltip
        open={isOpen && (isHovered || isClicked)}
        onOpenChange={(open) => {
          if (!isClicked) {
            setIsOpen(open);
          }
        }}>
        <TooltipTrigger
          asChild
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          <Button
            variant="ghost"
            onClick={handleCopy}
            className={cn(
              "p-0 text-sm text-indigo-300 hover:bg-transparent hover:text-indigo-400",
              className,
              {
                "h-min rounded-full bg-indigo-400/20 px-2 py-1 hover:bg-indigo-400/30 hover:text-indigo-300":
                  variant === "chip",
              }
            )}>
            <span className="truncate">{displayText}</span>
            <CopyIcon className="!size-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={0}
          className="border border-slate-400 bg-slate-700 px-2 py-1 text-xs text-slate-300">
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CopyToClipboardButton;
